import React, { useState, useRef, useEffect } from "react";
import axiosInstance from "../Axiosinstance/AxiosInstance";
import SuccessModal from "../common/SuccessModal";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import { Upload, X, Link as LinkIcon, Type, FileText, Image as ImageIcon, Loader2, Plus, Eye } from "lucide-react";

export default function ProjectCreate() {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", image: ""});
  const [errors, setErrors] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalMounted, setIsModalMounted] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProjects = async () => {
    try {
      setFetching(true);
      const res = await axiosInstance.get("service/api/v1/projects");
      setProjects(res.data || []);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setFetching(false);
    }
  };

  const openDeleteModal = (project) => {
    setDeleteTarget(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await axiosInstance.delete(`service/api/v1/projects/${deleteTarget.id}/`);
      setProjects((prev) => prev.filter((project) => project.id !== deleteTarget.id));
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete project:", error);
      alert("Failed to delete project.");
    } finally {
      setDeleting(false);
    }
  };

  const handleView = async (id) => {
    try {
      const res = await axiosInstance.get(`service/api/v1/projects/${id}/`);
      setForm(res.data);
      setEditingId(id);
      openModal();
    } catch (error) {
      console.error("Failed to fetch project:", error);
      alert("Failed to load project.");
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const openModal = () => {
    setIsModalMounted(true);
    // Small delay so CSS transitions can pick up state change cleanly
    setTimeout(() => {
      setIsModalOpen(true);
    }, 30);
  };

  const closeModal = (reset = true) => {
    setIsModalOpen(false);
    // Match this with the CSS transition duration for a smooth exit
    setTimeout(() => {
      setIsModalMounted(false);
      if (reset) {
        setEditingId(null);
        setForm({ name: "", description: "", image: "", link: "" });
      }
    }, 450);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateField = (name, value) => {
    const trimmed = value.trim();

    if (name === "name") {
      if (!trimmed) {
        return "Project name is required.";
      }
      if (trimmed.length < 3) {
        return "Project name must be at least 3 characters.";
      }
    }

    if (name === "description") {
      if (!trimmed) {
        return "Description is required.";
      }
      if (trimmed.length < 10) {
        return "Use at least 10 characters to describe the project.";
      }
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField("name", form.name),
      description: validateField("description", form.description),
    };

    setErrors(newErrors);

    return !newErrors.name && !newErrors.description;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("File size too large. Please select an image under 5MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const res = await axiosInstance.post("account/api/v1/upload-files", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Upload Response:", res.data);

      let imageUrl = "";
      if (res.data?.image_url) imageUrl = res.data.image_url;
      else if (res.data?.data?.url) imageUrl = res.data.data.url;
      else if (res.data?.url) imageUrl = res.data.url;
      else if (typeof res.data?.data === 'string') imageUrl = res.data.data;
      else if (typeof res.data === 'string') imageUrl = res.data;

      if (imageUrl) {
        setForm((prev) => ({ ...prev, image: imageUrl }));
      } else {
        console.error("Could not extract image URL from response:", res.data);
        alert("Upload successful but image URL not found.");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Image upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (e) => {
    if (e) e.preventDefault();
    setForm((prev) => ({ ...prev, image: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!form.image) {
      alert("Please upload an image first.");
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        // Update
        await axiosInstance.put(`service/api/v1/projects/${editingId}/`, form);
      } else {
        // Create
        await axiosInstance.post("service/api/v1/projects", form);
      }
      setShowSuccess(true);
      setForm({ name: "", description: "", image: "", link: "" });
      setEditingId(null);
      closeModal(false);
      fetchProjects();
    } catch (error) {
      console.error(editingId ? "Project update failed:" : "Project creation failed:", error);
      alert(editingId ? "Failed to update project." : "Failed to create project.");
    } finally {
      setLoading(false);
    }
  };

  return (
 <div class="w-full min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900
  text-white py-4 sm:py-6 lg:py-8 2xl:selection:bg-blue-500/30 selection:bg-blue-500/30 px-4">
      <div className="max-w-6xl 2xl:max-w-7xl mx-2">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 mb-8 md:mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1 tracking-tight">
              Projects
            </h1>
            <p className="text-gray-400 font-medium tracking-wide uppercase text-[11px]">
              Manage your portfolio showcase
            </p>
          </div>

          <button
            onClick={openModal}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-all shadow-[0_8px_20px_-8px_rgba(37,99,235,0.5)] active:scale-95 group"
          >
            <Plus size={18} className="group-hover:rotate-90 transition-transform duration-300" />
            <span>New Project</span>
          </button>
        </header>

        {fetching ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-gray-400 text-sm font-medium animate-pulse">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 bg-gray-900/70 rounded-xl border border-gray-700 shadow-lg">
            <div className="w-16 h-16 bg-gray-800/80 rounded-xl flex items-center justify-center text-gray-500 mx-auto mb-5">
              <ImageIcon size={32} />
            </div>
            <h3 className="text-xl font-semibold mb-1">No projects yet</h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto text-sm">
              Start building your portfolio by adding your first showcase project.
            </p>
            <button
              onClick={openModal}
              className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all border border-gray-600"
            >
              Add Your First Project
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg bg-gray-900/80 border border-gray-700 shadow-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-300">
                <thead className="bg-gray-800 text-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">Name</th>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">Description</th>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">Image</th>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id} className="border-t border-gray-700 hover:bg-gray-800/70">
                      <td className="px-6 py-4">
                        <h4 className="font-semibold text-gray-100">{project.name}</h4>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-400 line-clamp-1 max-w-xs">
                          {project.description}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-16 h-12 rounded-md overflow-hidden bg-gray-800 border border-gray-700 flex-shrink-0">
                          {project.image ? (
                            <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-600">
                              <ImageIcon size={18} />
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleView(project.id)}
                            className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white transition-all border border-blue-500/30"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(project)}
                            className="px-3 py-1.5 rounded-lg bg-red-500/10 text-xs font-medium text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/30"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-gray-900/90 border-t border-gray-700 flex items-center justify-between text-xs sm:text-sm text-gray-400">
              <p>
                Showing <span className="text-gray-200 font-semibold">{projects.length}</span> projects
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {isModalMounted && (
        <div
          className={`fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md transition-opacity duration-500 ease-out ${
            isModalOpen ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className={`relative w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide transform transition-all duration-500 ease-out ${
              isModalOpen
                ? "scale-100 translate-y-0 opacity-100"
                : "scale-95 translate-y-8 opacity-0"
            }`}
          >
            <button
              onClick={() => closeModal(true)}
              className="absolute top-6 right-6 z-50 p-3 bg-gray-800/80 hover:bg-white/10 text-white rounded-full transition-all border border-white/10 backdrop-blur-md"
            >
              <X size={24} />
            </button>

            <form
              onSubmit={handleSubmit}
              className="relative bg-gray-900 border border-white/10 p-6 sm:p-10 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>

              <div className="relative">
                <header className="mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 tracking-tight">
                    {editingId ? "Edit Project" : "Add New Project"}
                  </h2>
                  <p className="text-gray-400 font-medium uppercase text-[11px] tracking-widest">
                    Global Portfolio Showcase
                  </p>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-3 space-y-6">
                    <div>
                      <label className="flex items-center justify-between mb-2 ml-1">
                        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-300">
                          <Type size={14} className="text-blue-400" /> Project Name
                        </span>
                        <span className="text-[10px] font-medium text-blue-400/80 uppercase tracking-[0.18em]">
                          Required
                        </span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="e.g. Modern Dashboard UI"
                        className={`w-full px-5 py-3 rounded-2xl bg-gray-800/50 text-sm text-white border focus:outline-none focus:ring-2 focus:bg-gray-800 transition-all placeholder:text-gray-600 shadow-inner ${
                          errors.name
                            ? "border-red-500/70 focus:ring-red-500/40"
                            : "border-white/5 focus:ring-blue-500/40"
                        }`}
                      />
                      {errors.name && (
                        <p className="mt-1.5 ml-1 text-[11px] font-medium text-red-400">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center justify-between mb-2 ml-1">
                        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-300">
                          <FileText size={14} className="text-blue-400" /> Description
                        </span>
                        <span className="text-[10px] font-medium text-gray-400/90 tracking-[0.16em]">
                          Help visitors understand this project
                        </span>
                      </label>
                      <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={5}
                        placeholder="A brief overview of the project..."
                        className={`w-full px-5 py-3 rounded-2xl bg-gray-800/50 text-[13px] text-white border focus:outline-none focus:ring-2 focus:bg-gray-800 transition-all placeholder:text-gray-500 resize-y shadow-inner leading-relaxed min-h-[130px] sm:min-h-[150px] scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent ${
                          errors.description
                            ? "border-red-500/70 focus:ring-red-500/40"
                            : "border-white/5 focus:ring-blue-500/40"
                        }`}
                      />
                      {errors.description && (
                        <p className="mt-1.5 ml-1 text-[11px] font-medium text-red-400">{errors.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-2 flex flex-col">
                    <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 ml-1">
                      <ImageIcon size={14} className="text-blue-400" /> Cover Image
                    </label>

                    <div className="relative flex-1 group min-h-[260px]">
                      <input
                        ref={fileInputRef}
                        id="modal-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />

                      <label
                        htmlFor="modal-image-upload"
                        className={`flex flex-col items-center justify-center w-full h-full rounded-[2.5rem] border-2 border-dashed transition-all cursor-pointer overflow-hidden relative shadow-inner
                          ${form.image
                            ? "border-blue-500/30 bg-gray-800/30"
                            : "border-white/10 bg-gray-800/50 hover:bg-gray-800/80 hover:border-blue-500/50"
                          }
                          ${uploading ? "cursor-wait opacity-80" : ""}
                        `}
                      >
                        {form.image ? (
                          <div className="relative w-full h-full group">
                            <img
                              src={form.image}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                              <button
                                type="button"
                                onClick={removeImage}
                                className="p-4 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all shadow-lg"
                              >
                                <X size={24} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center flex flex-col items-center gap-5">
                            {uploading ? (
                              <div className="flex flex-col items-center gap-3">
                                <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                                <span className="text-blue-400 font-bold text-sm">Uploading Assets...</span>
                              </div>
                            ) : (
                              <>
                                <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 shadow-xl shadow-blue-500/10">
                                  <Upload size={28} />
                                </div>
                                <div>
                                  <p className="text-gray-100 font-semibold text-sm mb-1">Pick an Image</p>
                                  <p className="text-gray-500 text-[10px] tracking-[0.2em] font-black">The image size must be under 1 MB.</p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => closeModal(true)}
                    className="flex-1 py-4 px-6 text-sm font-semibold rounded-2xl bg-gray-800 hover:bg-gray-700 transition-all text-white border border-white/5 uppercase tracking-[0.2em]"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    disabled={loading || uploading}
                    className="flex-[2] py-4 px-6 text-sm font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_16px_40px_-18px_rgba(37,99,235,0.5)] active:scale-[0.98] flex items-center justify-center gap-3 uppercase tracking-[0.2em]"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <>{editingId ? "Update Project" : "Publish Project"}</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}


      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={editingId ? "Your project has been updated successfully." : "Your project has been published successfully."}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        title="Delete Project"
        description={`Are you sure you want to permanently delete "${deleteTarget?.name || "this project"}"? This action cannot be undone.`}
        confirmLabel="Delete Project"
        cancelLabel="Cancel"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (deleting) return;
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
